import React, { useState } from "react";
import { FiLock, FiAlertCircle, FiClock } from "react-icons/fi";
import { validatePasswordStrength } from "../../utils/crypto/vault-crypto";

interface VaultLockScreenProps {
  /** Callback when user enters master password */
  onUnlock: (password: string) => void;
  /** Number of seconds until auto-lock (for warning display) */
  secondsUntilLock?: number | null;
  /** Whether to show the warning banner */
  showWarning?: boolean;
  /** Title to display */
  title?: string;
  /** Description text */
  description?: string;
  /** Whether this is a re-lock (vs first time) */
  isRelock?: boolean;
}

/**
 * Lock screen component for vault session timeout
 * 
 * Displays when:
 * - User hasn't unlocked vault yet
 * - Session has timed out due to inactivity
 */
const VaultLockScreen: React.FC<VaultLockScreenProps> = ({
  onUnlock,
  secondsUntilLock,
  showWarning = false,
  title = "Vault Locked",
  description = "Enter your master password to access credentials",
  isRelock = false
}) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError("Please enter your master password");
      return;
    }

    const validation = validatePasswordStrength(password);
    if (!validation.isValid) {
      setError(validation.feedback[0] || "Password does not meet requirements");
      return;
    }

    setIsLoading(true);
    try {
      onUnlock(password);
      setPassword("");
    } catch (err: any) {
      setError(err.message || "Failed to unlock vault");
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
      {/* Lock Icon */}
      <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mb-6">
        <FiLock className="w-10 h-10 text-blue-600" />
      </div>

      {/* Title & Description */}
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-600 text-center mb-6 max-w-md">{description}</p>

      {/* Re-lock Notice */}
      {isRelock && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 max-w-md">
          <div className="flex items-start gap-2">
            <FiClock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Session Expired</p>
              <p className="text-amber-700">
                Your vault session has been locked due to inactivity for your security.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Warning Banner (shown when about to lock) */}
      {showWarning && secondsUntilLock != null && secondsUntilLock > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 max-w-md animate-pulse">
          <div className="flex items-center gap-2">
            <FiAlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              Vault will auto-lock in <strong>{formatTime(secondsUntilLock as number)}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Password Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-md">
        <div className="mb-4">
          <label htmlFor="master-password" className="block text-sm font-medium text-gray-700 mb-1">
            Master Password
          </label>
          <input
            id="master-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your master password"
            autoFocus
            autoComplete="current-password"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-800 text-sm">
              <FiAlertCircle className="w-4 h-4" />
              {error}
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !password}
          className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? "Unlocking..." : "Unlock Vault"}
        </button>
      </form>

      {/* Security Note */}
      <p className="mt-6 text-xs text-gray-500 text-center max-w-md">
        🔒 Your master password is never sent to our servers. All encryption and decryption happens locally in your browser.
      </p>

      {/* Master Password Loss Warning */}
      <div className="mt-4 max-w-md">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <FiAlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-700">
              <span className="font-medium">Forgot your password?</span> Unfortunately, we cannot recover your master password.
              If lost, your encrypted credentials cannot be accessed. Contact your administrator if you need to reset the vault.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaultLockScreen;

